/**
 * Promoting a permanent branch to the next one of its chain (plan « branches
 * protégées », step 5): the client of `GET /projects/:project/promotion` of
 * `waadoo-projects` (backend `plugins/waadooProjects/promotionRoute.ts`),
 * what the « Promotion » card makes of it, who is offered which button, and
 * the launch of the template `env-promote` (platform-catalog fork), run
 * by the backend action `waadoo:env:promote`
 * (`scaffolder/actions/environmentPower/promote.ts`).
 *
 * The types are re-declared rather than imported from the backend, as in
 * `permanentEnvironments.ts`: they are the contract of the route and change
 * on both sides at once — except a branch's `config`, the engine's
 * `EnvironmentConfigSummary` (`waadoo-config-common`, isomorphic), which
 * the backend sends as `summarizeEnvironmentConfig` makes it.
 *
 * UX ONLY: the buttons follow the rights the action enforces server-side —
 * the project's editors and the admin tier, a promotion TO the production for
 * the admin tier alone (`powerRight` on the target), nobody else — and the
 * chain the backend computed; a button shown wrongly is refused by the task.
 *
 * FAIL SOFT: the route exists only where `waadooProjects.gitlab.*` is set
 * (ludo), answers 404 to a caller without access, and GitLab may be down.
 * Every failure reads as « no promotion »: the card and the branch page's
 * shortcut are not shown.
 *
 * ONE READ PER PROJECT for the card and the branch page, shared for
 * {@link PROMOTION_TTL_MS} (`sharedLoad.ts`), per `FetchApi` instance.
 */

import { useEffect, useRef, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import type { ScaffolderApi } from '@backstage/plugin-scaffolder-react';
import type {
  AppProfile,
  EnvironmentConfigSummary,
} from '@internal/plugin-waadoo-config-common';
import { branchKind } from './branchKind';
import { powerRight, type PowerRight } from './environmentPower';
import type { ProjectAccessLevel } from './projectAccess';
import {
  createSharedLoad,
  type SharedLoad,
  type SharedLoadOptions,
} from './sharedLoad';

/** The backend plugin serving the route. */
const PLUGIN_ID = 'waadoo-projects';

/** The backend's own cache of the reads behind it (`PROMOTION_READ_TTL_MS`). */
export const PROMOTION_TTL_MS = 30_000;

/** Mirrored from the backend's `PromotionCommit`. */
export interface PromotionCommit {
  sha: string;
  shortSha: string;
  title: string;
  author?: string;
  date?: string;
}

export type { EnvironmentConfigSummary };

/** Mirrored from the backend's `PromotionBranch`. */
export interface PromotionBranch {
  name: string;
  active: boolean;
  head?: PromotionCommit;
  imageTag?: string;
  config?: EnvironmentConfigSummary;
}

/** Mirrored from the backend's `PromotionPair`. */
export interface PromotionPair {
  source: string;
  target: string;
  ahead: number;
  commits: PromotionCommit[];
  openMergeRequestUrl?: string;
  /**
   * The files of the diff that change what the target's pipeline runs
   * (backend `promotionGuard.ts`): an administrator's promotion only.
   */
  ciFiles: string[];
  /**
   * The jobs the target's CI would still run besides resolve-image and
   * promote (backend `jobsRunningOn`): a CI that still builds — an
   * administrator's promotion only.
   */
  buildingJobs: string[];
}

/** Mirrored from the backend's `PromotionResponse`. */
export interface PromotionResponse {
  /**
   * The project's profile (`developed` for a project without descriptor, as
   * the actions): a `standard` one has no image of its own — no `imageTag`
   * on its branches — and its promotion deploys the source's head.
   */
  profile: AppProfile;
  origin: 'annotation' | 'ci' | 'default';
  branches: PromotionBranch[];
  pairs: PromotionPair[];
}

interface Apis {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function isAnswer(value: unknown): value is PromotionResponse {
  return (
    isRecord(value) &&
    (value.profile === 'developed' || value.profile === 'standard') &&
    Array.isArray(value.branches) &&
    value.branches.every(
      branch =>
        isRecord(branch) &&
        typeof branch.name === 'string' &&
        typeof branch.active === 'boolean',
    ) &&
    Array.isArray(value.pairs) &&
    value.pairs.every(
      pair =>
        isRecord(pair) &&
        typeof pair.source === 'string' &&
        typeof pair.target === 'string' &&
        typeof pair.ahead === 'number' &&
        Array.isArray(pair.commits) &&
        Array.isArray(pair.ciFiles) &&
        Array.isArray(pair.buildingJobs),
    )
  );
}

/** Rejects on any non-2xx answer, or one of another shape. */
export async function fetchPromotion(
  apis: Apis & { project: string },
): Promise<PromotionResponse> {
  // Absolute backend URL: in development the frontend runs on another port.
  const base = await apis.discoveryApi.getBaseUrl(PLUGIN_ID);
  const response = await apis.fetchApi.fetch(
    `${base}/projects/${encodeURIComponent(apis.project)}/promotion`,
  );
  if (!response.ok) {
    throw new Error(`Promotion API: HTTP ${response.status}`);
  }
  const body: unknown = await response.json();
  if (!isAnswer(body)) {
    throw new Error('Promotion API: unexpected answer');
  }
  return body;
}

/** Never pruned: one entry per project read in the session, gone on reload. */
const loads = new WeakMap<
  FetchApi,
  Map<string, SharedLoad<PromotionResponse>>
>();

export function loadPromotion(
  apis: Apis & { project: string },
  options?: SharedLoadOptions,
): Promise<PromotionResponse> {
  let byProject = loads.get(apis.fetchApi);
  if (!byProject) {
    byProject = new Map();
    loads.set(apis.fetchApi, byProject);
  }
  let shared = byProject.get(apis.project);
  if (!shared) {
    shared = createSharedLoad<PromotionResponse>(PROMOTION_TTL_MS);
    byProject.set(apis.project, shared);
  }
  return shared.load(() => fetchPromotion(apis), options);
}

/**
 * The promotion facts of `project`: undefined while asked and for no
 * project, `null` on any failure (fail soft, header). `refreshKey`: a change
 * reads again past the shared answer.
 */
export function usePromotion(
  project: string | undefined,
  refreshKey?: number,
): PromotionResponse | null | undefined {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [answer, setAnswer] = useState<{
    project: string;
    promotion: PromotionResponse | null;
  }>();
  const lastRefresh = useRef(refreshKey);

  useEffect(() => {
    if (!project) {
      return undefined;
    }
    let active = true;
    const force = refreshKey !== lastRefresh.current;
    lastRefresh.current = refreshKey;
    const load = async () => {
      try {
        const promotion = await loadPromotion(
          { fetchApi, discoveryApi, project },
          { force },
        );
        if (active) setAnswer({ project, promotion });
      } catch {
        // Fail soft (header): no promotion offered.
        if (active) setAnswer({ project, promotion: null });
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [project, fetchApi, discoveryApi, refreshKey]);

  return answer && answer.project === project ? answer.promotion : undefined;
}

/** The pair `branch → next`, when `branch` has a next one in git. */
export function pairFrom(
  promotion: PromotionResponse,
  branch: string,
): PromotionPair | undefined {
  return promotion.pairs.find(pair => pair.source === branch);
}

/** The pair `previous → branch`. */
export function pairTo(
  promotion: PromotionResponse,
  branch: string,
): PromotionPair | undefined {
  return promotion.pairs.find(pair => pair.target === branch);
}

/**
 * What the caller gets on a promotion: the wake's and sleep's rule on its
 * target (`powerRight`) — the production locked for an editor, nothing for a
 * plain member —, and locked for an editor too when the promotion changes
 * the CI (`ciFiles`, which the action refuses to non-administrators).
 */
export function promotionRight(
  level: ProjectAccessLevel | undefined,
  pair: Pick<PromotionPair, 'target' | 'ciFiles' | 'buildingJobs'>,
): PowerRight {
  const right = powerRight(level, pair.target);
  return right === 'allowed' && promotionLockReason(level, pair)
    ? 'locked'
    : right;
}

/**
 * Why a promotion is locked for the caller: the CI change, when that is
 * the only reason; undefined for the production's « admins ».
 */
export function promotionLockReason(
  level: ProjectAccessLevel | undefined,
  pair: Pick<PromotionPair, 'target' | 'ciFiles' | 'buildingJobs'>,
): 'ci' | 'oldCi' | undefined {
  if (powerRight(level, pair.target) !== 'allowed' || level === 'admin') {
    return undefined;
  }
  if (pair.ciFiles.length > 0) {
    return 'ci';
  }
  return pair.buildingJobs.length > 0 ? 'oldCi' : undefined;
}

/**
 * The « Prochaine étape » of the card (mockup R3-A, 2026-10-19):
 * - `promote`: the first pair of the chain with commits ahead that the
 *   caller may promote (`promotionRight` « allowed »), its button offered;
 * - `adminOnly`: commits are ahead, but every such promotion is the
 *   administrators' for this editor (the production, a CI change) — the
 *   first of them, no button;
 * - `readOnly`: commits are ahead and the caller promotes nothing (a plain
 *   member, a level still unknown) — the first of them, no button;
 * - `upToDate`: no pair has commits ahead.
 *
 * In chain order: the backend lists the pairs from the first branch of the
 * chain to the last (`promotionRoute.ts`, one pair per adjacent branches).
 */
export type PromotionNextStep =
  | { kind: 'upToDate' }
  | { kind: 'promote' | 'adminOnly' | 'readOnly'; pair: PromotionPair };

export function promotionNextStep(
  level: ProjectAccessLevel | undefined,
  promotion: Pick<PromotionResponse, 'pairs'>,
): PromotionNextStep {
  const ahead = promotion.pairs.filter(pair => pair.ahead > 0);
  if (ahead.length === 0) {
    return { kind: 'upToDate' };
  }
  const allowed = ahead.find(pair => promotionRight(level, pair) === 'allowed');
  if (allowed) {
    return { kind: 'promote', pair: allowed };
  }
  return {
    kind: level === 'admin' || level === 'edit' ? 'adminOnly' : 'readOnly',
    pair: ahead[0],
  };
}

/** Whether a promotion to `target` asks for `<project>/<target>` typed. */
export function promotionNeedsConfirmation(target: string): boolean {
  return branchKind(target) === 'production';
}

/** Starts the promotion; resolves to the task's id. */
export async function launchPromotion(
  scaffolderApi: Pick<ScaffolderApi, 'scaffold'>,
  options: {
    /**
     * The template to run: the project's own for this role
     * (`useProjectTemplates`, components/shared/templateRoles.ts).
     */
    templateRef: string;
    project: string;
    source: string;
    target: string;
    confirmation?: string;
  },
): Promise<string> {
  const { taskId } = await scaffolderApi.scaffold({
    templateRef: options.templateRef,
    values: {
      project: options.project,
      source: options.source,
      target: options.target,
      ...(options.confirmation !== undefined
        ? { confirmation: options.confirmation }
        : {}),
    },
  });
  return taskId;
}

/** The tooltip of a promotion locked for the caller (`promotionLockReason`). */
export const LOCK_REASON_KEYS = {
  ci: 'promotion.ciLocked',
  oldCi: 'promotion.oldCiLocked',
} as const;
